import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface FinishTreatmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: number;
    token: string;
    onSuccess?: () => void;
}

export const FinishTreatmentModal: React.FC<FinishTreatmentModalProps> = ({
    isOpen,
    onClose,
    patientId,
    token,
    onSuccess,
}) => {
    const [cancelReason, setCancelReason] = useState('');
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setCancelReason('');
            setNotes('');
            setError(null);
        }
    }, [isOpen]);

    const handleSave = async () => {
        if (!cancelReason) {
            setError('Please select a finish reason');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await apiService.updateFinishStatus({
                token,
                patient_id: patientId,
                status: 'Finish',
                cancel_reason: cancelReason,
                notes: notes || '',
            });

            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error('Failed to update finish status:', err);
            setError(err.message || 'Failed to update finish status');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (!isSaving) {
            onClose();
        }
    };

    return (
        <ModalWrapper modalId="FinishTreatmentModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Finish Reason"
            isLoading={isSaving}
        >
            <div className="space-y-4">
                {/* Dropdown for cancel reason */}
                <div>
                    <select
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        disabled={isSaving}
                        className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75]"
                    >
                        <option value="">Select an option</option>
                        <option value="Clinic decision">Clinic decision</option>
                        <option value="Patient decision">Patient decision</option>
                    </select>
                </div>

                {/* Notes textarea */}
                <div>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={isSaving}
                        placeholder="Enter notes"
                        className="w-full px-4 py-3 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent resize-none text-[#366A75]"
                        rows={4}
                    />
                </div>

                {/* Error message */}
                {error && (
                    <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-center gap-4 pt-4">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
